import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MeasuringStrategy,
  MouseSensor,
  TouchSensor,
  closestCenter,
  pointerWithin,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type {
  CollisionDetection,
  DragEndEvent,
  DragMoveEvent,
  DragOverEvent,
  DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import type { PlayerListItem } from "../../shared/types/contracts";
import { LineupRowCard, LineupZone } from "./LineupEditorRows";
import {
  buildLineupPartition,
  findLineupContainer,
  inactivePlayerIds,
  moveLineupItem,
  reorderLineupItem,
} from "./lineupEditorLogic";
import type { LineupContainer, LineupPartition } from "./lineupEditorLogic";

interface LineupEditorProps {
  players: PlayerListItem[];
  activeIds: string[];
  lineupSlots: Array<string | null>;
  onCommit: (orderedIds: string[]) => boolean | Promise<boolean>;
  onSetAttendance: (
    playerId: string,
    active: boolean,
  ) => boolean | Promise<boolean>;
  saving?: boolean;
  mode?: "pregame" | "live" | "locked";
}

interface EventCoordinates {
  clientX?: number;
  clientY?: number;
  touches?: ArrayLike<{ clientX: number; clientY: number }>;
  changedTouches?: ArrayLike<{ clientX: number; clientY: number }>;
  nativeEvent?: EventCoordinates;
}

const collisionDetectionStrategy: CollisionDetection = (args) => {
  const intersections = pointerWithin(args);
  const rowIntersections = intersections.filter(
    ({ id }) => !String(id).endsWith("-dropzone"),
  );
  if (rowIntersections.length) return rowIntersections;
  return intersections.length ? intersections : closestCenter(args);
};

const measuring = { droppable: { strategy: MeasuringStrategy.Always } };

function sameIds(left: string[], right: string[]): boolean {
  return (
    left.length === right.length &&
    left.every((id, index) => id === right[index])
  );
}

function samePartition(left: LineupPartition, right: LineupPartition): boolean {
  return sameIds(left.lineup, right.lineup) && sameIds(left.bench, right.bench);
}

function eventPoint(event: Event): { x: number; y: number } | null {
  const candidate = event as Event & EventCoordinates;
  const source = candidate.nativeEvent ?? candidate;
  if (
    typeof source.clientX === "number" &&
    typeof source.clientY === "number"
  ) {
    return { x: source.clientX, y: source.clientY };
  }
  const touch = source.touches?.[0] ?? source.changedTouches?.[0];
  return touch ? { x: touch.clientX, y: touch.clientY } : null;
}

export function LineupEditor({
  players,
  activeIds,
  lineupSlots,
  onCommit,
  onSetAttendance,
  saving = false,
  mode = "pregame",
}: LineupEditorProps) {
  const reorderOnly = mode === "live" || mode === "locked";
  const readOnly = mode === "locked";
  const propPartition = useMemo(
    () => buildLineupPartition(players, lineupSlots, reorderOnly),
    [players, lineupSlots, reorderOnly],
  );
  const outSet = useMemo(
    () => inactivePlayerIds(players, activeIds),
    [players, activeIds],
  );
  const [items, setItems] = useState<LineupPartition>(propPartition);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const propPartitionRef = useRef(propPartition);
  propPartitionRef.current = propPartition;
  const latestDraftRef = useRef<LineupPartition | null>(null);
  const pendingCommitsRef = useRef(0);
  const draggingRef = useRef(false);
  const savingRef = useRef(saving);
  savingRef.current = saving;
  const syncFromProps = useCallback(() => {
    if (draggingRef.current || pendingCommitsRef.current > 0) return;
    if (latestDraftRef.current && savingRef.current) return;
    const incoming = propPartitionRef.current;
    latestDraftRef.current = null;
    if (samePartition(itemsRef.current, incoming)) return;
    itemsRef.current = incoming;
    setItems(incoming);
  }, []);
  useEffect(syncFromProps, [propPartition, saving, syncFromProps]);

  const restoreAfterDrag = () => {
    const previous = latestDraftRef.current ?? propPartitionRef.current;
    if (samePartition(itemsRef.current, previous)) return;
    itemsRef.current = previous;
    setItems(previous);
  };

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const findContainer = useCallback(
    (id: string) => findLineupContainer(itemsRef.current, id),
    [],
  );
  const [activeId, setActiveId] = useState<string | null>(null);
  const activeContainerRef = useRef<LineupContainer | null>(null);
  const lastOverIdRef = useRef<string | null>(null);
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
  const pointerCurrentRef = useRef<{ x: number; y: number } | null>(null);
  const zoneNodesRef = useRef<Record<LineupContainer, HTMLDivElement | null>>({
    lineup: null,
    bench: null,
  });
  const activeRecord = useMemo(() => {
    if (!activeId) return null;
    const player = players.find((candidate) => candidate.id === activeId);
    if (!player) return null;
    const from = items.lineup.includes(activeId) ? "lineup" : "bench";
    return { player, from } as const;
  }, [activeId, players, items]);

  const commit = useCallback(
    async (next: LineupPartition) => {
      if (readOnly) return;
      latestDraftRef.current = next;
      pendingCommitsRef.current += 1;
      setItems(next);
      itemsRef.current = next;
      let succeeded = false;
      try {
        const activeSet = new Set(activeIds);
        const toActivate = reorderOnly
          ? []
          : next.lineup.filter((id) => !activeSet.has(id));
        const attendance = Promise.all(
          toActivate.map((id) => onSetAttendance(id, true)),
        );
        const [attendanceResults, committed] = await Promise.all([
          attendance,
          onCommit(next.lineup),
        ]);
        succeeded = committed && attendanceResults.every(Boolean);
      } catch {
        succeeded = false;
      } finally {
        pendingCommitsRef.current -= 1;
        if (!succeeded && latestDraftRef.current === next) {
          latestDraftRef.current = null;
        }
        syncFromProps();
      }
    },
    [
      activeIds,
      onCommit,
      onSetAttendance,
      readOnly,
      reorderOnly,
      syncFromProps,
    ],
  );

  const handleDragStart = (event: DragStartEvent) => {
    if (readOnly) return;
    draggingRef.current = true;
    const id = String(event.active.id);
    const point = eventPoint(event.activatorEvent);
    activeContainerRef.current = findContainer(id);
    lastOverIdRef.current = null;
    pointerStartRef.current = point;
    pointerCurrentRef.current = point;
    setActiveId(id);
  };

  const handleDragMove = ({ delta }: DragMoveEvent) => {
    const start = pointerStartRef.current;
    if (!start) return;
    pointerCurrentRef.current = {
      x: start.x + delta.x,
      y: start.y + delta.y,
    };
  };

  const handleDragOver = ({ active, over }: DragOverEvent) => {
    if (readOnly || !over) return;
    const playerId = String(active.id);
    const targetId = String(over.id);
    if (targetId !== playerId) lastOverIdRef.current = targetId;
    const source = findContainer(playerId);
    const destination = findContainer(targetId);
    if (!source || !destination || source === destination || reorderOnly)
      return;
    if (!itemsRef.current[source].includes(playerId)) return;
    const next = moveLineupItem(
      itemsRef.current,
      playerId,
      destination,
      targetId,
    );
    setItems(next);
    itemsRef.current = next;
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (readOnly) return;
    draggingRef.current = false;
    setActiveId(null);
    const startedIn = activeContainerRef.current;
    const pointer = pointerCurrentRef.current;
    activeContainerRef.current = null;
    pointerStartRef.current = null;
    pointerCurrentRef.current = null;
    const playerId = String(active.id);
    const overId = over ? String(over.id) : null;
    const targetId =
      overId && overId !== playerId ? overId : lastOverIdRef.current;
    lastOverIdRef.current = null;
    const source = findContainer(playerId);
    const overContainer = targetId ? findContainer(targetId) : null;
    const translated = active.rect.current.translated;
    const dropPoint =
      pointer ??
      (translated
        ? {
            x: translated.left + translated.width / 2,
            y: translated.top + translated.height / 2,
          }
        : null);
    const rectContainer = dropPoint
      ? ((["lineup", "bench"] as const).find((container) => {
          const rect = zoneNodesRef.current[container]?.getBoundingClientRect();
          return (
            rect &&
            dropPoint.x >= rect.left &&
            dropPoint.x <= rect.right &&
            dropPoint.y >= rect.top &&
            dropPoint.y <= rect.bottom
          );
        }) ?? null)
      : null;
    const destination = rectContainer ?? overContainer ?? source;
    if (!source || !destination) {
      restoreAfterDrag();
      return;
    }
    if (reorderOnly && destination !== source) {
      restoreAfterDrag();
      return;
    }
    if (startedIn && startedIn !== destination) {
      void commit(
        moveLineupItem(itemsRef.current, playerId, destination, targetId),
      );
      return;
    }
    if (!overContainer || source !== overContainer) {
      restoreAfterDrag();
      return;
    }
    const next = reorderLineupItem(
      itemsRef.current,
      source,
      playerId,
      targetId,
    );
    if (next !== itemsRef.current) {
      void commit(next);
    } else {
      restoreAfterDrag();
    }
  };

  const handleDragCancel = () => {
    draggingRef.current = false;
    activeContainerRef.current = null;
    lastOverIdRef.current = null;
    pointerStartRef.current = null;
    pointerCurrentRef.current = null;
    setActiveId(null);
    restoreAfterDrag();
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetectionStrategy}
      measuring={measuring}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext
        id="lineup"
        items={items.lineup}
        strategy={verticalListSortingStrategy}
      >
        <LineupZone
          zoneId="lineup"
          setZoneRef={(node) => {
            zoneNodesRef.current.lineup = node;
          }}
          title="LINEUP"
          count={items.lineup.length}
          emptyLabel="Drag players here to set the batting order"
          rows={items.lineup.map((id, index) => ({
            id,
            orderIndex: index + 1,
          }))}
          players={players}
          outSet={outSet}
          tone="lineup"
          readOnly={readOnly}
        />
      </SortableContext>

      {!reorderOnly ? (
        <>
          <div
            aria-hidden="true"
            data-testid="lineup-bench-divider"
            className="mx-1.5 my-1.5 h-px bg-border/70"
          />
          <SortableContext
            id="bench"
            items={items.bench}
            strategy={verticalListSortingStrategy}
          >
            <LineupZone
              zoneId="bench"
              setZoneRef={(node) => {
                zoneNodesRef.current.bench = node;
              }}
              title="BENCH"
              count={items.bench.length}
              emptyLabel="Drag players here to sit them out"
              rows={items.bench.map((id) => ({ id, orderIndex: null }))}
              players={players}
              outSet={outSet}
              tone="bench"
              readOnly={readOnly}
            />
          </SortableContext>
        </>
      ) : null}

      <DragOverlay dropAnimation={null} style={{ pointerEvents: "none" }}>
        {activeRecord ? (
          <LineupRowCard
            player={activeRecord.player}
            orderIndex={null}
            inLineup={activeRecord.from === "lineup"}
            isOut={outSet.has(activeRecord.player.id)}
            dragOverlay
            readOnly={readOnly}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
